import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { FileText, Plus, Check, X, Filter, Camera, Sparkles, Loader2, Upload, ScanLine, Zap, Search, DollarSign, TrendingUp, Clock, Users, Eye, RotateCcw, MessageSquare, Paperclip, ExternalLink, AlertTriangle, CheckCircle2, XCircle, Download, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import * as XLSX from 'xlsx';

export default function Claims() {
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState('all');
  const [activeTab, setActiveTab] = useState('my-claims');
  const [isScanning, setIsScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  // Iter95 (Ankur 15-Jul-2026) — lock the Submit button while the POST is
  // in-flight so a double-click cannot create duplicate claim rows. The
  // backend also has a 60-second dedupe guard as belt + braces.
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  // TC-CLM analytics: clickable Total/Pending/Approved/Rejected cards.
  // Values: 'all' | 'pending' | 'approved' | 'rejected'
  const [selectedStatus, setSelectedStatus] = useState('all');
  // Date-range filter (composes with search/year/month/status/category)
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const DEFAULT_YEAR = new Date().getFullYear().toString();
  const hrFiltersActive = Boolean(searchQuery) || filterYear !== DEFAULT_YEAR || filterMonth !== 'all'
    || Boolean(dateFrom) || Boolean(dateTo) || selectedCategory !== 'all' || selectedStatus !== 'all';
  const resetHRFilters = () => {
    setSearchQuery(''); setFilterYear(DEFAULT_YEAR); setFilterMonth('all');
    setDateFrom(''); setDateTo(''); setSelectedCategory('all'); setSelectedStatus('all');
  };
  // Edit-claim dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editClaim, setEditClaim] = useState(null);
  const [editForm, setEditForm] = useState({ claim_type: 'medical', amount: '', description: '', claim_date: '', receipt_url: '' });
  const [editReceiptFileName, setEditReceiptFileName] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const editReceiptInputRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    claim_type: 'medical',
    amount: '',
    description: '',
    claim_date: new Date().toISOString().split('T')[0],
    receipt_url: ''
  });
  const [receiptFileName, setReceiptFileName] = useState('');
  
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isHRAdmin = ['admin', 'hr_manager'].includes(user.role);
  const isManager = ['department_head', 'manager'].includes(user.role);
  const canReview = ['admin', 'hr_manager', 'department_head', 'manager'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [claimsRes, empRes] = await Promise.all([
        axios.get(`${API}/claims`),
        axios.get(`${API}/employees`)
      ]);
      setClaims(claimsRes.data);
      setEmployees(empRes.data);
      
      if (user.role === 'employee' && empRes.data.length > 0) {
        const currentUser = empRes.data.find(e => e.employee_id === user.employee_id);
        if (currentUser) {
          setFormData(prev => ({ ...prev, employee_id: currentUser.employee_id }));
        }
      }
    } catch (error) {
      toast.error('Failed to fetch claims');
    } finally {
      setLoading(false);
    }
  };

  // Shared date-range filter: applies year/month/from/to. Used by BOTH
  // the analytics cards AND the table so all surfaces stay in sync.
  const claimsInDateRange = useMemo(() => {
    return claims.filter(claim => {
      if (!claim.claim_date) return false;
      const claimDate = new Date(claim.claim_date);
      if (Number.isNaN(claimDate.getTime())) return false;

      const year = claimDate.getFullYear().toString();
      const month = (claimDate.getMonth() + 1).toString().padStart(2, '0');
      if (filterYear !== 'all' && year !== filterYear) return false;
      if (filterMonth !== 'all' && month !== filterMonth) return false;

      // Custom date range (inclusive on both ends). Compare on date-only.
      const dateOnly = claim.claim_date.length >= 10 ? claim.claim_date.slice(0, 10) : claim.claim_date;
      if (dateFrom && dateOnly < dateFrom) return false;
      if (dateTo && dateOnly > dateTo) return false;

      return true;
    });
  }, [claims, filterYear, filterMonth, dateFrom, dateTo]);

  // Claims analytics for HR Admin — reflects current date-range filter so the
  // cards & table stay consistent.
  const claimsAnalytics = useMemo(() => {
    const src = claimsInDateRange;
    // Iter48 — count any claim in mid-approval workflow as "pending" so the
    // analytics card matches the dashboard and the Reports preview.
    const PENDING_STATUSES = new Set(['pending', 'hr_checked', 'submitted', 'under_review', 'in_review']);
    const analytics = {
      total: src.length,
      totalAmount: src.reduce((sum, c) => sum + (c.amount || 0), 0),
      pending: src.filter(c => PENDING_STATUSES.has(c.status)),
      approved: src.filter(c => c.status === 'approved'),
      rejected: src.filter(c => c.status === 'rejected'),
      byCategory: {
        medical: { claims: src.filter(c => c.claim_type === 'medical'), amount: 0 },
        travel: { claims: src.filter(c => c.claim_type === 'travel'), amount: 0 },
        entertainment: { claims: src.filter(c => c.claim_type === 'entertainment'), amount: 0 },
        others: { claims: src.filter(c => c.claim_type === 'others'), amount: 0 }
      }
    };
    Object.keys(analytics.byCategory).forEach(cat => {
      analytics.byCategory[cat].amount = analytics.byCategory[cat].claims.reduce((sum, c) => sum + (c.amount || 0), 0);
    });
    return analytics;
  }, [claimsInDateRange]);

  // Filter claims for HR Admin view — start from the date-windowed set so the
  // table matches the analytics cards. Then layer on category/status/search.
  const filteredHRClaims = useMemo(() => {
    let filtered = claimsInDateRange;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(c => c.claim_type === selectedCategory);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(c => c.status === selectedStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(claim => {
        const employee = employees.find(e => e.employee_id === claim.employee_id);
        return (
          employee?.full_name?.toLowerCase().includes(query) ||
          claim.employee_id?.toLowerCase().includes(query) ||
          employee?.email?.toLowerCase().includes(query) ||
          claim.description?.toLowerCase().includes(query)
        );
      });
    }
    
    return filtered;
  }, [claimsInDateRange, selectedCategory, selectedStatus, searchQuery, employees]);

  // Export currently filtered HR claims to Excel (.xlsx)
  const exportFilteredClaimsToExcel = () => {
    if (!filteredHRClaims || filteredHRClaims.length === 0) {
      toast.error('No claims to export with current filters');
      return;
    }
    try {
      const rows = filteredHRClaims.map((c) => {
        const employee = employees.find((e) => e.employee_id === c.employee_id);
        return {
          'Employee Name': c.employee_name || employee?.full_name || 'Unknown',
          'Claim ID': c.claim_id || '',
          'Category': c.claim_type || '',
          'Amount (RM)': Number(c.amount || 0),
          'Date': c.claim_date || '',
          'Status': c.status || '',
          'Description': c.description || '',
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      // Column widths for readability
      ws['!cols'] = [
        { wch: 24 }, // Employee Name
        { wch: 38 }, // Claim ID
        { wch: 16 }, // Category
        { wch: 14 }, // Amount
        { wch: 12 }, // Date
        { wch: 12 }, // Status
        { wch: 40 }, // Description
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Claims');
      const today = new Date().toISOString().slice(0, 10);
      const fname = `claims_export_${today}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success(`Exported ${rows.length} claim${rows.length === 1 ? '' : 's'} to ${fname}`);
    } catch (err) {
      console.error('Excel export failed', err);
      toast.error('Excel export failed');
    }
  };

  const handleReceiptScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Accept images AND PDFs. AI scan is only attempted for images.
    const isImage = (file.type || '').startsWith('image/');
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (!isImage && !isPdf) {
      toast.error('Please upload an image (JPG/PNG) or PDF receipt');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB allowed.');
      return;
    }

    setIsScanning(true);
    toast.info(isPdf ? 'PDF receipt uploaded.' : 'Receipt uploaded. Attempting AI scan...');

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;

        // Always retain the receipt as a data URL in formData so the claim can be
        // submitted manually even if AI scanning is disabled / fails.
        setFormData(prev => ({ ...prev, receipt_url: base64 }));
        setReceiptFileName(file.name);

        // Skip AI scanning for PDF — only images are OCR-able by our scanner.
        if (isPdf) {
          toast.success(`Receipt attached (${file.name}). Please fill the form manually.`);
          setIsScanning(false);
          return;
        }

        try {
          const token = localStorage.getItem('hrms_token');
          const response = await axios.post(
            `${API}/ai/scan-receipt`,
            { image_base64: base64 },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            const data = response.data.data;
            setScannedData(data);
            
            setFormData(prev => ({
              ...prev,
              amount: data.amount?.toString() || prev.amount,
              description: data.description || `${data.vendor_name || ''} - ${data.category || ''}`.trim(),
              claim_type: mapCategory(data.category),
              claim_date: data.date && data.date !== 'unknown' ? data.date : prev.claim_date
            }));

            toast.success(`Receipt scanned! Amount: RM ${data.amount?.toFixed(2) || '0.00'}`);
          }
        } catch (error) {
          console.error('Scan error:', error);
          const errorMsg = error.response?.data?.detail || 'Failed to scan receipt';
          if (errorMsg.includes('AI features are disabled') || errorMsg.includes('AI service not configured')) {
            toast.info('AI scanning is not enabled. Receipt saved — please fill the form manually.');
          } else {
            toast.message('Receipt saved. AI scan failed, please fill manually.');
          }
        } finally {
          setIsScanning(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setIsScanning(false);
      toast.error('Error processing image');
    }
  };

  const mapCategory = (category) => {
    const categoryMap = {
      'medical': 'medical',
      'travel': 'travel',
      'entertainment': 'entertainment',
      'food': 'entertainment',
      'office_supplies': 'others',
      'others': 'others'
    };
    return categoryMap[category?.toLowerCase()] || 'others';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client-side validation: receipt must be attached before hitting the backend.
    if (!formData.receipt_url) {
      toast.error('Receipt attachment is required');
      return;
    }
    // Iter95 — Guard against double-click. The button is also `disabled`
    // while `submitting` is true, but this early-return covers the edge
    // case where the click event fires twice before React re-renders.
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/claims`, {
        ...formData,
        amount: parseFloat(formData.amount),
        employee_id: user.employee_id || formData.employee_id,
        claim_id: `CLM-${Date.now()}`,
        status: 'pending',
        submitted_at: new Date().toISOString()
      });
      // Iter95 — Backend returns `duplicate: true` when the same signature
      // was already submitted in the last 60 seconds (dedupe guard).
      if (res?.data?.duplicate) {
        toast.info('This claim was already submitted a moment ago — no duplicate created.', { duration: 6000 });
        setDialogOpen(false);
        setScannedData(null);
        setReceiptFileName('');
        setFormData({
          employee_id: user.employee_id || '',
          claim_type: 'medical',
          amount: '',
          description: '',
          claim_date: new Date().toISOString().split('T')[0],
          receipt_url: ''
        });
        fetchData();
        return;
      }
      // TC-CLM-010: backend surfaces the monthly-limit (RM 500) breach via the
      // `X-Claim-Warning` response header. Show it prominently so the employee
      // is aware the claim may require additional approval. We surface ONLY the
      // warning when present (instead of stacking it with a success toast,
      // which Sonner pushes off-screen).
      const warning = res?.headers?.['x-claim-warning'] || res?.headers?.['X-Claim-Warning'];
      if (warning) {
        toast.warning(`Claim submitted. ${warning}`, { duration: 10000 });
      } else {
        // Iter72 — TC-CLM-032: confirm email notification dispatched.
        toast.success('Claim submitted. Notification email sent to you and your manager.', { duration: 5000 });
      }
      setDialogOpen(false);
      setScannedData(null);
      setReceiptFileName('');
      setFormData({
        employee_id: user.employee_id || '',
        claim_type: 'medical',
        amount: '',
        description: '',
        claim_date: new Date().toISOString().split('T')[0],
        receipt_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReview = async (claimId, action) => {
    try {
      await axios.put(`${API}/claims/${claimId}/review?action=${action}`);
      // Iter72 — TC-CLM-033/034: confirm email notification dispatched.
      toast.success(`Claim ${action}d successfully. Notification email sent to the employee.`, { duration: 5000 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} claim`);
    }
  };

  // Open claim detail modal
  const openClaimDetail = async (claim) => {
    setSelectedClaim(claim);
    setDetailDialogOpen(true);
  };

  // ---- Edit Claim (pending-only) ----
  // Editable when status is pending / hr_checked / returned AND user is owner or HR/admin.
  const canEditClaim = (claim) => {
    if (!claim) return false;
    const editableStatuses = new Set(['pending', 'hr_checked', 'returned']);
    if (!editableStatuses.has(claim.status)) return false;
    const isOwner = claim.employee_id === user.employee_id;
    const isHR = ['admin', 'hr_manager', 'super_user'].includes(user.role);
    return isOwner || isHR;
  };

  const openEditClaim = (claim) => {
    setEditClaim(claim);
    setEditForm({
      claim_type: claim.claim_type || 'medical',
      amount: claim.amount != null ? String(claim.amount) : '',
      description: claim.description || '',
      claim_date: (claim.claim_date || '').slice(0, 10),
      receipt_url: claim.receipt_url || '',
    });
    setEditReceiptFileName('');
    setEditDialogOpen(true);
  };

  const handleEditReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = (file.type || '').startsWith('image/');
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    if (!isImage && !isPdf) {
      toast.error('Please upload an image (JPG/PNG) or PDF receipt');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB allowed.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setEditForm(prev => ({ ...prev, receipt_url: reader.result }));
      setEditReceiptFileName(file.name);
      toast.success(`Receipt replaced (${file.name})`);
    };
    reader.readAsDataURL(file);
  };

  const submitEditClaim = async () => {
    if (!editClaim) return;
    if (!editForm.amount || parseFloat(editForm.amount) <= 0) {
      toast.error('Amount must be greater than zero');
      return;
    }
    if (!editForm.claim_date) {
      toast.error('Date is required');
      return;
    }
    if (!(editForm.description || '').trim()) {
      toast.error('Description is required');
      return;
    }
    if (!editForm.receipt_url) {
      toast.error('Receipt attachment is required');
      return;
    }
    setEditSaving(true);
    try {
      await axios.put(`${API}/claims/${editClaim.claim_id}`, {
        claim_type: editForm.claim_type,
        amount: parseFloat(editForm.amount),
        description: editForm.description,
        claim_date: editForm.claim_date,
        receipt_url: editForm.receipt_url,
      });
      toast.success('Claim updated');
      setEditDialogOpen(false);
      setEditClaim(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update claim');
    } finally {
      setEditSaving(false);
    }
  };

  // Open review dialog with action
  const openReviewDialog = (claim, action) => {
    setSelectedClaim(claim);
    setReviewAction(action);
    setReviewComments('');
    setReviewDialogOpen(true);
  };

  // Submit review with comments
  const submitReview = async () => {
    if (!selectedClaim || !reviewAction) return;
    
    // Require comments for rejection or return
    if ((reviewAction === 'rejected' || reviewAction === 'returned') && !reviewComments.trim()) {
      toast.error('Please provide remarks for rejection or return');
      return;
    }
    
    setReviewLoading(true);
    try {
      const stage = isHRAdmin ? 'hr_check' : 'manager_approval';
      const params = new URLSearchParams({
        action: reviewAction,
        stage: stage
      });
      if (reviewComments.trim()) {
        params.append('comments', reviewComments.trim());
      }
      
      await axios.put(`${API}/claims/${selectedClaim.claim_id}/review?${params.toString()}`);
      
      const actionLabel = {
        'checked': 'checked and forwarded to manager',
        'approved': 'approved',
        'rejected': 'rejected',
        'returned': 'returned to employee'
      }[reviewAction] || reviewAction;
      
      // Iter72 — TC-CLM-033/034: confirm email notification dispatched.
      toast.success(
        `Claim ${actionLabel} successfully. Notification email sent to the employee.`,
        { duration: 5000 }
      );
      setReviewDialogOpen(false);
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${reviewAction} claim`);
    } finally {
      setReviewLoading(false);
    }
  };

  // Get status badge styling
  const getStatusBadge = (claim) => {
    const status = claim.status;
    const styles = {
      'pending': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', label: 'Pending HR Check' },
      'hr_checked': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Pending Manager Approval' },
      'approved': { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', label: 'Approved' },
      'rejected': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', label: 'Rejected' },
      'returned': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', label: 'Returned for Correction' }
    };
    return styles[status] || styles['pending'];
  };

  // Iter88f — Derive the Approval Workflow step statuses from the
  // authoritative top-level `claim.status` when the sub-status fields
  // (`hr_check_status`, `manager_approval_status`) are missing.
  //
  // Reported by Ankur 12-Jun-2026: Claims table showed "Approved" but the
  // detail modal showed Step 1 "Pending" / Step 2 "Waiting for HR". Root
  // cause: legacy/demo-seeded claims were written with `status='approved'`
  // but null sub-status fields, so the modal had no way to render the
  // workflow correctly. This helper makes the modal trust the top-level
  // status as the source of truth.
  const deriveWorkflowSteps = (claim) => {
    if (!claim) return { hr: 'pending', manager: 'pending' };
    const status = (claim.status || 'pending').toLowerCase();
    let hr = claim.hr_check_status;
    let manager = claim.manager_approval_status;

    if (status === 'approved') {
      hr = hr || 'checked';
      manager = manager || 'approved';
    } else if (status === 'rejected') {
      // Honour whichever stage rejected; if neither sub-field set, attribute
      // to whichever stage we know was reached.
      if (hr === 'rejected') {
        manager = manager || 'waiting';
      } else if (manager === 'rejected') {
        hr = hr || 'checked';
      } else {
        // Fall back: assume HR rejection unless we have evidence of HR check.
        if (hr === 'checked') {
          manager = manager || 'rejected';
        } else {
          hr = hr || 'rejected';
        }
      }
    } else if (status === 'returned') {
      hr = hr || 'returned';
    } else if (status === 'hr_checked') {
      hr = hr || 'checked';
      manager = manager || 'pending';
    } else {
      // pending → no checks performed yet
      hr = hr || 'pending';
    }
    return { hr, manager };
  };

  // Iter88i — Render claim attachments safely. Receipts are stored as
  // base64 data URLs (data:image/png;base64,...), which modern browsers
  // BLOCK from opening in new tabs as anti-phishing protection. Result:
  // `<a href={dataUrl} target="_blank">` clicks silently no-op in Chrome
  // and Edge. Reported by tester ~S 15-Jun-2026.
  //
  // Strategy:
  //   - If it's an image data URL → render inline as a clickable thumbnail
  //     that expands to full-size (works in every browser, no popup).
  //   - Otherwise (PDF, HTTP URL, raw text URL) → trigger a programmatic
  //     download via Blob URL when clicked, which sidesteps the data:
  //     popup restriction.
  const downloadDataUrl = (url, suggestedName = 'receipt') => {
    if (!url) return;
    try {
      // For data: URLs we materialise a blob and download/open via blob URL,
      // which Chrome WILL allow in a new tab.
      if (url.startsWith('data:')) {
        const [meta, b64] = url.split(',');
        const mime = (meta.match(/data:([^;]+)/) || [, 'application/octet-stream'])[1];
        const bin = atob(b64 || '');
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        const blobUrl = URL.createObjectURL(blob);
        // Open the blob URL in a new tab. Browser allows this; user can save from there.
        const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
        // Safety net: if popup was blocked, fall back to forced download.
        if (!w) {
          const link = document.createElement('a');
          link.href = blobUrl;
          const ext = (mime.split('/')[1] || 'bin').split('+')[0];
          link.download = `${suggestedName}.${ext}`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        // Revoke after a short delay so the new tab has time to load.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } else {
        // Regular http(s) URL — opening in a new tab works as normal.
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      // Last-resort fallback
      console.error('Failed to open attachment:', err);
      toast.error('Could not open attachment. Try downloading instead.');
    }
  };

  const isImageDataUrl = (url) => typeof url === 'string' && /^data:image\//i.test(url);

  const renderAttachment = (url, label, key) => {
    if (!url) return null;
    if (isImageDataUrl(url)) {
      return (
        <div
          key={key}
          data-testid={`claim-attachment-${key ?? 0}`}
          className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg space-y-2"
        >
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-violet-500" />
            <span className="text-slate-900 dark:text-slate-100 flex-1">{label}</span>
            <button
              type="button"
              onClick={() => downloadDataUrl(url, label.replace(/\s+/g, '_'))}
              className="text-xs text-violet-600 hover:text-violet-700 underline"
            >
              Open full size
            </button>
          </div>
          <img
            src={url}
            alt={label}
            className="max-h-72 w-auto rounded border border-slate-200 dark:border-slate-700 mx-auto cursor-zoom-in"
            onClick={() => downloadDataUrl(url, label.replace(/\s+/g, '_'))}
          />
        </div>
      );
    }
    return (
      <button
        type="button"
        key={key}
        onClick={() => downloadDataUrl(url, label.replace(/\s+/g, '_'))}
        data-testid={`claim-attachment-${key ?? 0}`}
        className="w-full flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-left"
      >
        <FileText className="h-5 w-5 text-violet-500" />
        <span className="text-slate-900 dark:text-slate-100 flex-1">{label}</span>
        <ExternalLink className="h-4 w-4 text-slate-400" />
      </button>
    );
  };


  const myClaims = claims.filter(claim => claim.employee_id === user.employee_id);
  const teamClaims = claims.filter(claim => {
    if (!isManager) return false;
    const isTeamClaim = claim.manager_id === user.employee_id || 
      employees.find(e => e.employee_id === claim.employee_id)?.manager_id === user.employee_id;
    return isTeamClaim && claim.employee_id !== user.employee_id;
  });

  const getFilteredClaims = () => {
    let claimsToFilter = claims;
    
    if (isManager) {
      claimsToFilter = activeTab === 'my-claims' ? myClaims : teamClaims;
    } else {
      claimsToFilter = myClaims;
    }
    
    return claimsToFilter.filter(claim => {
      const claimDate = new Date(claim.claim_date);
      const year = claimDate.getFullYear().toString();
      const month = (claimDate.getMonth() + 1).toString().padStart(2, '0');
      
      if (filterYear !== 'all' && year !== filterYear) return false;
      if (filterMonth !== 'all' && month !== filterMonth) return false;
      return true;
    });
  };
  
  const filteredClaims = getFilteredClaims();
  const availableYears = [...new Set(claims.map(c => new Date(c.claim_date).getFullYear().toString()))].sort().reverse();

  const getCategoryColor = (category) => {
    const colors = {
      medical: { bg: 'from-blue-500 to-cyan-500', light: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
      travel: { bg: 'from-emerald-500 to-teal-500', light: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
      entertainment: { bg: 'from-purple-500 to-pink-500', light: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
      others: { bg: 'from-slate-500 to-gray-500', light: 'bg-slate-100 dark:bg-slate-900/30', text: 'text-slate-600 dark:text-slate-400' }
    };
    return colors[category] || colors.others;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 dark:border-slate-100"></div></div>;
  }

  // HR Admin Analytics View
  if (isHRAdmin) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Claims Analytics</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Monitor and review employee expense claims</p>
          </div>
        </div>

        {/* Summary Cards — clickable to filter the claims table by status */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            type="button"
            onClick={() => setSelectedStatus('all')}
            data-testid="analytics-card-total"
            aria-pressed={selectedStatus === 'all'}
            className={`text-left bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 text-white transition-all hover:brightness-110 focus:outline-none ${
              selectedStatus === 'all' ? 'ring-4 ring-violet-300 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-900' : ''
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-5 w-5 text-violet-200" />
              <span className="text-violet-100 text-sm font-medium">Total Claims Value</span>
            </div>
            <p className="text-3xl font-bold">RM {claimsAnalytics.totalAmount.toFixed(2)}</p>
            <p className="text-violet-200 text-sm mt-1">{claimsAnalytics.total} total claims</p>
          </button>

          <button
            type="button"
            onClick={() => setSelectedStatus(selectedStatus === 'pending' ? 'all' : 'pending')}
            data-testid="analytics-card-pending"
            aria-pressed={selectedStatus === 'pending'}
            className={`text-left bg-white dark:bg-slate-800 rounded-2xl p-5 border-2 transition-all hover:border-amber-400 focus:outline-none ${
              selectedStatus === 'pending'
                ? 'border-amber-500 ring-2 ring-amber-200 dark:ring-amber-900/40'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Pending Review</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{claimsAnalytics.pending.length}</p>
            <p className="text-slate-500 text-sm mt-1">
              RM {claimsAnalytics.pending.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setSelectedStatus(selectedStatus === 'approved' ? 'all' : 'approved')}
            data-testid="analytics-card-approved"
            aria-pressed={selectedStatus === 'approved'}
            className={`text-left bg-white dark:bg-slate-800 rounded-2xl p-5 border-2 transition-all hover:border-emerald-400 focus:outline-none ${
              selectedStatus === 'approved'
                ? 'border-emerald-500 ring-2 ring-emerald-200 dark:ring-emerald-900/40'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Check className="h-5 w-5 text-emerald-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Approved</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{claimsAnalytics.approved.length}</p>
            <p className="text-slate-500 text-sm mt-1">
              RM {claimsAnalytics.approved.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setSelectedStatus(selectedStatus === 'rejected' ? 'all' : 'rejected')}
            data-testid="analytics-card-rejected"
            aria-pressed={selectedStatus === 'rejected'}
            className={`text-left bg-white dark:bg-slate-800 rounded-2xl p-5 border-2 transition-all hover:border-red-400 focus:outline-none ${
              selectedStatus === 'rejected'
                ? 'border-red-500 ring-2 ring-red-200 dark:ring-red-900/40'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <X className="h-5 w-5 text-red-500" />
              <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Rejected</span>
            </div>
            <p className="text-3xl font-bold text-red-600">{claimsAnalytics.rejected.length}</p>
            <p className="text-slate-500 text-sm mt-1">
              RM {claimsAnalytics.rejected.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2)}
            </p>
          </button>
        </div>

        {/* Claims by Category */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Claims by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(claimsAnalytics.byCategory).map(([category, data]) => {
              const colors = getCategoryColor(category);
              const isSelected = selectedCategory === category;
              
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(isSelected ? 'all' : category)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected 
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors.bg} flex items-center justify-center mb-3`}>
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400 capitalize">{category}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{data.claims.length}</p>
                  <p className="text-sm text-slate-500 mt-1">RM {data.amount.toFixed(2)}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Filters — responsive grid keeps inputs aligned on
            desktop and wraps cleanly on smaller screens. */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
            {/* Search - takes full width on small, span 4 cols on large */}
            <div className="lg:col-span-4">
              <Label htmlFor="claims-search" className="text-xs text-slate-500 dark:text-slate-400">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="claims-search"
                  data-testid="claims-search-input"
                  placeholder="Name, employee ID, or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 pl-9 dark:bg-slate-900 dark:border-slate-600"
                  style={{ borderRadius: '12px' }}
                />
              </div>
            </div>

            <div className="lg:col-span-1">
              <Label className="text-xs text-slate-500 dark:text-slate-400">Year</Label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="mt-1 h-10 w-full px-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
                style={{ borderRadius: '12px' }}
              >
                <option value="all">All</option>
                {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>

            <div className="lg:col-span-2">
              <Label className="text-xs text-slate-500 dark:text-slate-400">Month</Label>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="mt-1 h-10 w-full px-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
                style={{ borderRadius: '12px' }}
              >
                <option value="all">All Months</option>
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                  <option key={m} value={(i + 1).toString().padStart(2, '0')}>{m}</option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <Label htmlFor="claims-date-from" className="text-xs text-slate-500 dark:text-slate-400">From</Label>
              <Input
                id="claims-date-from"
                data-testid="claims-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="mt-1 h-10 w-full dark:bg-slate-900 dark:border-slate-600 text-sm"
                style={{ borderRadius: '12px' }}
              />
            </div>

            <div className="lg:col-span-2">
              <Label htmlFor="claims-date-to" className="text-xs text-slate-500 dark:text-slate-400">To</Label>
              <Input
                id="claims-date-to"
                data-testid="claims-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="mt-1 h-10 w-full dark:bg-slate-900 dark:border-slate-600 text-sm"
                style={{ borderRadius: '12px' }}
              />
            </div>

            <div className="lg:col-span-1 flex">
              <Button
                type="button"
                onClick={exportFilteredClaimsToExcel}
                data-testid="claims-export-excel"
                className="h-10 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                style={{ borderRadius: '12px' }}
              >
                <Download className="h-4 w-4 mr-1" />
                Excel
              </Button>
            </div>
          </div>

          {/* Reset all filters — one click back to the default view */}
          {hrFiltersActive && (
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={resetHRFilters}
                data-testid="claims-reset-filters"
                className="h-9 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600"
                style={{ borderRadius: '12px' }}
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Reset all filters
              </Button>
            </div>
          )}

          {/* Active-filter chips row — only shown when a filter is set */}
          {((dateFrom || dateTo) || selectedCategory !== 'all' || selectedStatus !== 'all') && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  data-testid="claims-clear-date-range"
                  className="text-violet-600 hover:text-violet-700 underline"
                >
                  Clear date range
                </button>
              )}
              {selectedCategory !== 'all' && (
                <button
                  onClick={() => setSelectedCategory('all')}
                  className="text-violet-600 hover:text-violet-700 underline"
                >
                  Clear category filter
                </button>
              )}
              {selectedStatus !== 'all' && (
                <button
                  type="button"
                  onClick={() => setSelectedStatus('all')}
                  className="text-violet-600 hover:text-violet-700 underline"
                >
                  Clear status filter
                </button>
              )}
            </div>
          )}
        </div>

        {/* Claims Table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100" data-testid="claims-table-heading">
                {selectedStatus !== 'all'
                  ? `${selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)} Claims`
                  : selectedCategory !== 'all'
                  ? `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Claims`
                  : 'All Claims'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="claims-table-count">{filteredHRClaims.length} claims found</p>
            </div>
            <div className="flex items-center gap-2">
              {selectedStatus !== 'all' && (
                <button
                  type="button"
                  onClick={() => setSelectedStatus('all')}
                  data-testid="clear-status-filter"
                  className="text-sm text-violet-600 hover:text-violet-700"
                >
                  Clear status filter
                </button>
              )}
              <span className="bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-3 py-1 rounded-full text-sm font-medium" data-testid="hr-pending-review-pill">
                {claims.filter(c => ['pending', 'hr_checked', 'submitted', 'under_review', 'in_review'].includes(c.status)).length} pending review
              </span>
            </div>
          </div>

          {filteredHRClaims.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                {searchQuery ? 'No claims found matching your search' : 'No claims found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Employee</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Category</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Amount</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Date</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Description</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Status</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {filteredHRClaims.map(claim => {
                    const employee = employees.find(e => e.employee_id === claim.employee_id);
                    const colors = getCategoryColor(claim.claim_type);
                    
                    return (
                      <tr key={claim.claim_id} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {employee?.full_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-slate-100">{employee?.full_name || 'Unknown'}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">{claim.employee_id}</p>
                              <p className="text-xs text-slate-400">{employee?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${colors.light} ${colors.text}`}>
                            {claim.claim_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            RM {claim.amount?.toFixed(2) || '0.00'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300">
                          {new Date(claim.claim_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 max-w-xs truncate">
                          {claim.description || 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const badge = getStatusBadge(claim);
                            return (
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                                {badge.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            {/* View Details Button - Always visible */}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openClaimDetail(claim)} 
                              className="h-8 border-slate-300 dark:border-slate-600" 
                              style={{ borderRadius: '8px' }}
                              data-testid={`view-claim-${claim.claim_id}`}
                            >
                              <Eye className="h-3 w-3 mr-1" />View
                            </Button>

                            {/* Edit Button - For editable pending claims only */}
                            {canEditClaim(claim) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditClaim(claim)}
                                className="h-8 border-violet-300 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                                style={{ borderRadius: '8px' }}
                                data-testid={`edit-claim-${claim.claim_id}`}
                              >
                                <Pencil className="h-3 w-3 mr-1" />Edit
                              </Button>
                            )}
                            
                            {/* HR Check Actions - For pending claims */}
                            {claim.status === 'pending' && (
                              <>
                                <Button 
                                  size="sm" 
                                  onClick={() => openReviewDialog(claim, 'checked')} 
                                  className="bg-blue-600 hover:bg-blue-700 h-8" 
                                  style={{ borderRadius: '8px' }}
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Check
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openReviewDialog(claim, 'returned')} 
                                  className="h-8 border-orange-300 text-orange-600 hover:bg-orange-50" 
                                  style={{ borderRadius: '8px' }}
                                >
                                  <RotateCcw className="h-3 w-3 mr-1" />Return
                                </Button>
                              </>
                            )}
                            
                            {/* Iter80 — Once the claim has progressed to
                                "Pending Manager Approval" (hr_checked),
                                HR Manager has already done their job and
                                should NOT see Approve/Reject — that's the
                                manager's call. We keep the controls for
                                `admin` (super-user escalation only) and
                                show an info chip to hr_managers so they
                                understand WHY the controls are gone.
                                Reported by Ankur 03-Jun-2026. */}
                            {claim.status === 'hr_checked' && user.role === 'admin' && (
                              <>
                                <Button 
                                  size="sm" 
                                  onClick={() => openReviewDialog(claim, 'approved')} 
                                  className="bg-emerald-600 hover:bg-emerald-700 h-8" 
                                  style={{ borderRadius: '8px' }}
                                >
                                  <Check className="h-3 w-3 mr-1" />Approve
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  onClick={() => openReviewDialog(claim, 'rejected')} 
                                  className="h-8" 
                                  style={{ borderRadius: '8px' }}
                                >
                                  <X className="h-3 w-3 mr-1" />Reject
                                </Button>
                              </>
                            )}
                            {claim.status === 'hr_checked' && user.role === 'hr_manager' && (
                              <span
                                className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                                title="HR check is complete. The reporting manager must take the final decision."
                                data-testid={`awaiting-manager-${claim.claim_id}`}
                              >
                                Awaiting manager
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Claim Detail Modal */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl bg-white dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <FileText className="h-5 w-5 text-violet-500" />
                Claim Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedClaim && (
              <div className="space-y-6">
                {/* Claim Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Employee</label>
                    <p className="text-slate-900 dark:text-slate-100 font-medium">
                      {employees.find(e => e.employee_id === selectedClaim.employee_id)?.full_name || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Category</label>
                    <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">{selectedClaim.claim_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Amount</label>
                    <p className="text-2xl font-bold text-emerald-600">RM {selectedClaim.amount?.toFixed(2)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Date</label>
                    <p className="text-slate-900 dark:text-slate-100">
                      {new Date(selectedClaim.claim_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                
                {/* Description */}
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Description</label>
                  <p className="text-slate-900 dark:text-slate-100 mt-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                    {selectedClaim.description || 'No description provided'}
                  </p>
                </div>
                
                {/* Attachments */}
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments / Receipts
                  </label>
                  <div className="mt-2 space-y-2">
                    {selectedClaim.receipt_url ? (
                      renderAttachment(selectedClaim.receipt_url, 'Receipt / Supporting Document', 0)
                    ) : selectedClaim.attachments?.length > 0 ? (
                      selectedClaim.attachments.map((url, idx) =>
                        renderAttachment(url, `Attachment ${idx + 1}`, idx)
                      )
                    ) : (
                      <p className="text-slate-500 dark:text-slate-400 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                        No attachments uploaded
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Status & Workflow */}
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Approval Workflow</label>
                  <div className="mt-2 space-y-3">
                    {(() => { const wf = deriveWorkflowSteps(selectedClaim); return (<>
                    {/* HR Check Status */}
                    <div className={`p-3 rounded-lg border ${
                      wf.hr === 'checked' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20' :
                      wf.hr === 'rejected' || wf.hr === 'returned' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                      'border-amber-300 bg-amber-50 dark:bg-amber-900/20'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 dark:text-slate-100">Step 1: HR Check</span>
                        <span className={`text-sm font-medium capitalize ${
                          wf.hr === 'checked' ? 'text-emerald-600' :
                          wf.hr === 'rejected' || wf.hr === 'returned' ? 'text-red-600' :
                          'text-amber-600'
                        }`}>
                          {wf.hr || 'Pending'}
                        </span>
                      </div>
                      {selectedClaim.hr_check_comments && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                          <strong>Comments:</strong> {selectedClaim.hr_check_comments}
                        </p>
                      )}
                    </div>
                    
                    {/* Manager Approval Status */}
                    <div className={`p-3 rounded-lg border ${
                      wf.manager === 'approved' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20' :
                      wf.manager === 'rejected' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                      wf.hr === 'checked' ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' :
                      'border-slate-200 bg-slate-50 dark:bg-slate-900'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 dark:text-slate-100">Step 2: Manager Approval</span>
                        <span className={`text-sm font-medium capitalize ${
                          wf.manager === 'approved' ? 'text-emerald-600' :
                          wf.manager === 'rejected' ? 'text-red-600' :
                          wf.hr === 'checked' ? 'text-amber-600' :
                          'text-slate-400'
                        }`}>
                          {wf.manager || (wf.hr === 'checked' ? 'Pending' : 'Waiting for HR')}
                        </span>
                      </div>
                      {selectedClaim.reviewer_comments && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                          <strong>Comments:</strong> {selectedClaim.reviewer_comments}
                        </p>
                      )}
                    </div>
                    </>); })()}
                  </div>
                </div>
                
                {/* Rejection Remarks */}
                {selectedClaim.rejection_remarks && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800 dark:text-red-300">Rejection/Return Remarks</p>
                        <p className="text-sm text-red-700 dark:text-red-400 mt-1">{selectedClaim.rejection_remarks}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Action Buttons */}
                <DialogFooter className="flex gap-2">
                  {selectedClaim.status === 'pending' && (
                    <>
                      <Button 
                        onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedClaim, 'checked'); }}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />Check & Forward
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedClaim, 'returned'); }}
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />Return to Employee
                      </Button>
                    </>
                  )}
                  {/* Iter80 — Same restriction inside the Claim Detail
                      Modal: HR Manager loses Approve/Reject once the claim
                      reaches manager approval stage. Admin keeps controls
                      as a super-user escalation path. */}
                  {selectedClaim.status === 'hr_checked' && user.role === 'admin' && (
                    <>
                      <Button 
                        onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedClaim, 'approved'); }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4 mr-2" />Approve
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedClaim, 'rejected'); }}
                      >
                        <X className="h-4 w-4 mr-2" />Reject
                      </Button>
                    </>
                  )}
                  {selectedClaim.status === 'hr_checked' && user.role === 'hr_manager' && (
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                      title="HR check is complete. The reporting manager must take the final decision."
                    >
                      Awaiting reporting manager's decision
                    </span>
                  )}
                  <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Review Dialog with Comments */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-md bg-white dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
                {reviewAction === 'checked' && <CheckCircle2 className="h-5 w-5 text-blue-500" />}
                {reviewAction === 'approved' && <Check className="h-5 w-5 text-emerald-500" />}
                {reviewAction === 'rejected' && <XCircle className="h-5 w-5 text-red-500" />}
                {reviewAction === 'returned' && <RotateCcw className="h-5 w-5 text-orange-500" />}
                {reviewAction === 'checked' && 'Check & Forward Claim'}
                {reviewAction === 'approved' && 'Approve Claim'}
                {reviewAction === 'rejected' && 'Reject Claim'}
                {reviewAction === 'returned' && 'Return Claim for Correction'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedClaim && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Amount:</strong> RM {selectedClaim.amount?.toFixed(2)}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Type:</strong> {selectedClaim.claim_type}
                  </p>
                </div>
              )}
              
              <div>
                <Label className="text-slate-900 dark:text-slate-100">
                  {(reviewAction === 'rejected' || reviewAction === 'returned') ? 'Remarks (Required)' : 'Comments (Optional)'}
                </Label>
                <Textarea
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  placeholder={
                    reviewAction === 'rejected' ? 'Please provide reason for rejection...' :
                    reviewAction === 'returned' ? 'Please specify what needs to be corrected...' :
                    'Add any comments...'
                  }
                  className="mt-2 min-h-[100px] dark:bg-slate-900 dark:border-slate-600"
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitReview}
                  disabled={reviewLoading || ((reviewAction === 'rejected' || reviewAction === 'returned') && !reviewComments.trim())}
                  className={
                    reviewAction === 'checked' ? 'bg-blue-600 hover:bg-blue-700' :
                    reviewAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' :
                    reviewAction === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-orange-600 hover:bg-orange-700'
                  }
                >
                  {reviewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {reviewAction === 'checked' && 'Check & Forward'}
                  {reviewAction === 'approved' && 'Approve'}
                  {reviewAction === 'rejected' && 'Reject'}
                  {reviewAction === 'returned' && 'Return'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Iter70 — Edit Claim Dialog for HR Admin view.
            Previously the Edit Dialog was only rendered in the
            Employee/Manager return block (line ~1500), so when HR Admin
            clicked the "Edit" pill on a pending/hr_checked/returned claim
            the dialog never showed and the click looked dead.
            Reported by Ankur 01-Jun-2026. */}
        <Dialog open={editDialogOpen} onOpenChange={(o) => { setEditDialogOpen(o); if (!o) setEditClaim(null); }}>
          <DialogContent className="max-w-lg bg-white dark:bg-slate-800" style={{ borderRadius: '24px' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center text-slate-900 dark:text-slate-100">
                <Pencil className="h-5 w-5 mr-2 text-violet-500" />
                Edit Pending Claim
              </DialogTitle>
            </DialogHeader>
            {editClaim && (
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Claim Type</Label>
                  <select
                    data-testid="edit-claim-type"
                    value={editForm.claim_type}
                    onChange={(e) => setEditForm({ ...editForm, claim_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    style={{ borderRadius: '12px' }}
                  >
                    <option value="medical">Medical</option>
                    <option value="travel">Travel</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="others">Others</option>
                  </select>
                </div>
                <div>
                  <Label>Amount (RM)</Label>
                  <Input
                    data-testid="edit-claim-amount"
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    placeholder="0.00"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input
                    data-testid="edit-claim-date"
                    type="date"
                    value={editForm.claim_date}
                    onChange={(e) => setEditForm({ ...editForm, claim_date: e.target.value })}
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    data-testid="edit-claim-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    placeholder="Brief description"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label>Receipt (replace optional)</Label>
                  <input
                    ref={editReceiptInputRef}
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    onChange={handleEditReceiptUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => editReceiptInputRef.current?.click()}
                      data-testid="edit-claim-replace-receipt"
                      style={{ borderRadius: '12px' }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Replace receipt
                    </Button>
                    <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {editReceiptFileName || (editForm.receipt_url ? 'Existing receipt attached' : 'No receipt')}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} style={{ borderRadius: '12px' }}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    data-testid="edit-claim-save"
                    onClick={submitEditClaim}
                    disabled={editSaving}
                    className="bg-violet-600 hover:bg-violet-700"
                    style={{ borderRadius: '12px' }}
                  >
                    {editSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Employee/Manager View (Original)
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Claims & Reimbursements</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {isManager && activeTab === 'team-claims' 
              ? 'Review and approve team expense claims' 
              : 'Submit and track your expense claims'}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700" style={{ borderRadius: '12px' }}>
          <Sparkles className="h-4 w-4 mr-2" />
          AI-Powered Claim
        </Button>
      </div>

      {/* Tabs for Managers */}
      {isManager && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1.5" style={{ borderRadius: '16px' }}>
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('my-claims')}
              className={`flex-1 px-6 py-3 text-sm font-semibold transition-all ${
                activeTab === 'my-claims'
                  ? 'bg-slate-900 dark:bg-emerald-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              style={{ borderRadius: '12px' }}
            >
              My Own Claims
              {myClaims.length > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs ${
                  activeTab === 'my-claims' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`} style={{ borderRadius: '8px' }}>
                  {myClaims.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('team-claims')}
              className={`flex-1 px-6 py-3 text-sm font-semibold transition-all ${
                activeTab === 'team-claims'
                  ? 'bg-slate-900 dark:bg-emerald-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              style={{ borderRadius: '12px' }}
            >
              My Team Claims
              {teamClaims.length > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs ${
                  activeTab === 'team-claims' 
                    ? 'bg-white/20 text-white' 
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`} style={{ borderRadius: '8px' }}>
                  {teamClaims.length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4" style={{ borderRadius: '16px' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filter Claims</h3>
          </div>
          {(filterYear !== DEFAULT_YEAR || filterMonth !== 'all') && (
            <button
              type="button"
              onClick={() => { setFilterYear(DEFAULT_YEAR); setFilterMonth('all'); }}
              data-testid="my-claims-reset-filters"
              className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset all filters
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Year</label>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" style={{ borderRadius: '12px' }}>
              <option value="all">All Years</option>
              {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Month</label>
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" style={{ borderRadius: '12px' }}>
              <option value="all">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                <option key={m} value={(i + 1).toString().padStart(2, '0')}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ borderRadius: '16px' }}>
        {filteredClaims.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No claims found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Employee</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Type</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Amount</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Date</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Status</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredClaims.map(claim => {
                const badge = getStatusBadge(claim);
                return (
                <tr 
                  key={claim.claim_id} 
                  className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">{employees.find(e => e.employee_id === claim.employee_id)?.full_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100 capitalize">{claim.claim_type}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-slate-100">RM {claim.amount.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">{new Date(claim.claim_date).toLocaleDateString('en-MY')}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium ${badge.bg} ${badge.text}`} style={{ borderRadius: '8px' }}>
                      {badge.label}
                    </span>
                    {claim.rejection_remarks && (
                      <p className="text-xs text-red-500 mt-1 max-w-[150px] truncate" title={claim.rejection_remarks}>
                        {claim.rejection_remarks}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      {/* View Button - Always visible */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openClaimDetail(claim)} 
                        className="h-8 border-slate-300 dark:border-slate-600" 
                        style={{ borderRadius: '8px' }}
                      >
                        <Eye className="h-3 w-3 mr-1" />View
                      </Button>

                      {/* Edit Button - For editable pending claims only */}
                      {canEditClaim(claim) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditClaim(claim)}
                          className="h-8 border-violet-300 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30"
                          style={{ borderRadius: '8px' }}
                          data-testid={`edit-claim-${claim.claim_id}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" />Edit
                        </Button>
                      )}
                      
                      {/* Manager approval actions for team claims that are HR checked */}
                      {isManager && activeTab === 'team-claims' && claim.employee_id !== user.employee_id && claim.status === 'hr_checked' && (
                        <>
                          <Button size="sm" onClick={() => openReviewDialog(claim, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 h-8" style={{ borderRadius: '8px' }}>
                            <Check className="h-3 w-3 mr-1" />Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openReviewDialog(claim, 'rejected')} className="h-8" style={{ borderRadius: '8px' }}>
                            <X className="h-3 w-3 mr-1" />Reject
                          </Button>
                        </>
                      )}
                      
                      {/* Resubmit button for returned claims (own claims only) */}
                      {claim.status === 'returned' && claim.employee_id === user.employee_id && (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setFormData({
                              employee_id: claim.employee_id,
                              claim_type: claim.claim_type,
                              amount: claim.amount.toString(),
                              description: claim.description,
                              claim_date: claim.claim_date
                            });
                            setDialogOpen(true);
                          }} 
                          className="bg-orange-600 hover:bg-orange-700 h-8" 
                          style={{ borderRadius: '8px' }}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />Resubmit
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Claim Dialog — pending claims only */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => { setEditDialogOpen(o); if (!o) setEditClaim(null); }}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-800" style={{ borderRadius: '24px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center text-slate-900 dark:text-slate-100">
              <Pencil className="h-5 w-5 mr-2 text-violet-500" />
              Edit Pending Claim
            </DialogTitle>
          </DialogHeader>
          {editClaim && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Claim Type</Label>
                <select
                  data-testid="edit-claim-type"
                  value={editForm.claim_type}
                  onChange={(e) => setEditForm({ ...editForm, claim_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  style={{ borderRadius: '12px' }}
                >
                  <option value="medical">Medical</option>
                  <option value="travel">Travel</option>
                  <option value="entertainment">Entertainment</option>
                  <option value="others">Others</option>
                </select>
              </div>
              <div>
                <Label>Amount (RM)</Label>
                <Input
                  data-testid="edit-claim-amount"
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  placeholder="0.00"
                  style={{ borderRadius: '12px' }}
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  data-testid="edit-claim-date"
                  type="date"
                  value={editForm.claim_date}
                  onChange={(e) => setEditForm({ ...editForm, claim_date: e.target.value })}
                  style={{ borderRadius: '12px' }}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  data-testid="edit-claim-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Brief description"
                  style={{ borderRadius: '12px' }}
                />
              </div>
              <div>
                <Label>Receipt (replace optional)</Label>
                <input
                  ref={editReceiptInputRef}
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  onChange={handleEditReceiptUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => editReceiptInputRef.current?.click()}
                    data-testid="edit-claim-replace-receipt"
                    style={{ borderRadius: '12px' }}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Replace receipt
                  </Button>
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {editReceiptFileName || (editForm.receipt_url ? 'Existing receipt attached' : 'No receipt')}
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} style={{ borderRadius: '12px' }}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  data-testid="edit-claim-save"
                  onClick={submitEditClaim}
                  disabled={editSaving}
                  className="bg-violet-600 hover:bg-violet-700"
                  style={{ borderRadius: '12px' }}
                >
                  {editSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Submit Claim Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" style={{ borderRadius: '24px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-violet-500" />
              AI-Powered Claim Submission
            </DialogTitle>
          </DialogHeader>
          
          {/* AI Receipt Scanner */}
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 p-4 border border-violet-200 dark:border-violet-800" style={{ borderRadius: '16px' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <ScanLine className="h-5 w-5 text-violet-600" />
                <span className="font-semibold text-violet-900 dark:text-violet-100">AI Receipt Scanner</span>
              </div>
              <span className="text-xs bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 px-2 py-1" style={{ borderRadius: '6px' }}>
                <Zap className="h-3 w-3 inline mr-1" />OCR Powered
              </span>
            </div>
            <p className="text-sm text-violet-700 dark:text-violet-300 mb-3">
              Upload a receipt (image or PDF). AI will auto-extract details from images.
            </p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleReceiptScan}
              accept="image/*,application/pdf,.pdf"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isScanning}
              className="w-full border-violet-300 dark:border-violet-600 hover:bg-violet-100 dark:hover:bg-violet-800"
              style={{ borderRadius: '12px' }}
            >
              {isScanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning Receipt...
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  Upload Receipt (Image / PDF)
                </>
              )}
            </Button>
            
            {scannedData && (
              <div className="mt-3 p-3 bg-white dark:bg-slate-800 text-sm" style={{ borderRadius: '12px' }}>
                <p className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center">
                  <Check className="h-4 w-4 mr-1" />
                  Receipt scanned successfully!
                </p>
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  Vendor: {scannedData.vendor_name} • Confidence: {scannedData.confidence}
                </p>
              </div>
            )}

            {/* Receipt-attached indicator (shown whether or not AI scan succeeded) */}
            {!scannedData && formData.receipt_url && (
              <div className="mt-3 p-3 bg-white dark:bg-slate-800 text-sm" style={{ borderRadius: '12px' }} data-testid="receipt-attached-indicator">
                <p className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center">
                  <Check className="h-4 w-4 mr-1" />
                  Receipt attached
                </p>
                {receiptFileName && (
                  <p className="text-slate-600 dark:text-slate-400 mt-1 truncate">{receiptFileName}</p>
                )}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Claim Type</Label>
              <select 
                value={formData.claim_type} 
                onChange={(e) => setFormData({ ...formData, claim_type: e.target.value })} 
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                style={{ borderRadius: '12px' }}
              >
                <option value="medical">Medical</option>
                <option value="travel">Travel</option>
                <option value="entertainment">Entertainment</option>
                <option value="others">Others</option>
              </select>
            </div>
            <div>
              <Label>Amount (RM)</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={formData.amount} 
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })} 
                required 
                placeholder="0.00"
                style={{ borderRadius: '12px' }}
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input 
                type="date" 
                value={formData.claim_date} 
                onChange={(e) => setFormData({ ...formData, claim_date: e.target.value })} 
                required 
                style={{ borderRadius: '12px' }}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                required 
                placeholder="Brief description of expense"
                style={{ borderRadius: '12px' }}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setScannedData(null); setReceiptFileName(''); setFormData(prev => ({ ...prev, receipt_url: '' })); }} style={{ borderRadius: '12px' }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-claim-btn" disabled={submitting} className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed" style={{ borderRadius: '12px' }}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Submit Claim
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Claim Detail Modal (Employee/Manager View) */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-500" />
              Claim Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedClaim && (
            <div className="space-y-6">
              {/* Claim Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Employee</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">
                    {employees.find(e => e.employee_id === selectedClaim.employee_id)?.full_name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Category</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">{selectedClaim.claim_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Amount</label>
                  <p className="text-2xl font-bold text-emerald-600">RM {selectedClaim.amount?.toFixed(2)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Date</label>
                  <p className="text-slate-900 dark:text-slate-100">
                    {new Date(selectedClaim.claim_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
              
              {/* Description */}
              <div>
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Description</label>
                <p className="text-slate-900 dark:text-slate-100 mt-1 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  {selectedClaim.description || 'No description provided'}
                </p>
              </div>
              
              {/* Attachments */}
              <div>
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachments / Receipts
                </label>
                <div className="mt-2 space-y-2">
                  {selectedClaim.receipt_url ? (
                    renderAttachment(selectedClaim.receipt_url, 'Receipt / Supporting Document', 0)
                  ) : selectedClaim.attachments?.length > 0 ? (
                    selectedClaim.attachments.map((url, idx) =>
                      renderAttachment(url, `Attachment ${idx + 1}`, idx)
                    )
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      No attachments uploaded
                    </p>
                  )}
                </div>
              </div>
              
              {/* Status & Workflow */}
              <div>
                <label className="text-sm font-medium text-slate-500 dark:text-slate-400">Approval Workflow</label>
                <div className="mt-2 space-y-3">
                  {(() => { const wf = deriveWorkflowSteps(selectedClaim); return (<>
                  {/* HR Check Status */}
                  <div className={`p-3 rounded-lg border ${
                    wf.hr === 'checked' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20' :
                    wf.hr === 'rejected' || wf.hr === 'returned' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                    'border-amber-300 bg-amber-50 dark:bg-amber-900/20'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900 dark:text-slate-100">Step 1: HR Check</span>
                      <span className={`text-sm font-medium capitalize ${
                        wf.hr === 'checked' ? 'text-emerald-600' :
                        wf.hr === 'rejected' || wf.hr === 'returned' ? 'text-red-600' :
                        'text-amber-600'
                      }`}>
                        {wf.hr || 'Pending'}
                      </span>
                    </div>
                    {selectedClaim.hr_check_comments && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        <strong>Comments:</strong> {selectedClaim.hr_check_comments}
                      </p>
                    )}
                  </div>
                  
                  {/* Manager Approval Status */}
                  <div className={`p-3 rounded-lg border ${
                    wf.manager === 'approved' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20' :
                    wf.manager === 'rejected' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
                    wf.hr === 'checked' ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' :
                    'border-slate-200 bg-slate-50 dark:bg-slate-900'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900 dark:text-slate-100">Step 2: Manager Approval</span>
                      <span className={`text-sm font-medium capitalize ${
                        wf.manager === 'approved' ? 'text-emerald-600' :
                        wf.manager === 'rejected' ? 'text-red-600' :
                        wf.hr === 'checked' ? 'text-amber-600' :
                        'text-slate-400'
                      }`}>
                        {wf.manager || (wf.hr === 'checked' ? 'Pending' : 'Waiting for HR')}
                      </span>
                    </div>
                    {selectedClaim.reviewer_comments && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        <strong>Comments:</strong> {selectedClaim.reviewer_comments}
                      </p>
                    )}
                  </div>
                  </>); })()}
                </div>
              </div>
              
              {/* Rejection Remarks */}
              {selectedClaim.rejection_remarks && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-300">Rejection/Return Remarks</p>
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1">{selectedClaim.rejection_remarks}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <DialogFooter className="flex gap-2">
                {/* Manager approval actions */}
                {isManager && selectedClaim.status === 'hr_checked' && selectedClaim.employee_id !== user.employee_id && (
                  <>
                    <Button 
                      onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedClaim, 'approved'); }}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Check className="h-4 w-4 mr-2" />Approve
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => { setDetailDialogOpen(false); openReviewDialog(selectedClaim, 'rejected'); }}
                    >
                      <X className="h-4 w-4 mr-2" />Reject
                    </Button>
                  </>
                )}
                
                {/* Resubmit for returned claims */}
                {selectedClaim.status === 'returned' && selectedClaim.employee_id === user.employee_id && (
                  <Button 
                    onClick={() => {
                      setDetailDialogOpen(false);
                      setFormData({
                        employee_id: selectedClaim.employee_id,
                        claim_type: selectedClaim.claim_type,
                        amount: selectedClaim.amount.toString(),
                        description: selectedClaim.description,
                        claim_date: selectedClaim.claim_date
                      });
                      setDialogOpen(true);
                    }}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />Resubmit Claim
                  </Button>
                )}
                
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Review Dialog with Comments (Employee/Manager View) */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              {reviewAction === 'approved' && <Check className="h-5 w-5 text-emerald-500" />}
              {reviewAction === 'rejected' && <XCircle className="h-5 w-5 text-red-500" />}
              {reviewAction === 'returned' && <RotateCcw className="h-5 w-5 text-orange-500" />}
              {reviewAction === 'approved' && 'Approve Claim'}
              {reviewAction === 'rejected' && 'Reject Claim'}
              {reviewAction === 'returned' && 'Return Claim for Correction'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedClaim && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Amount:</strong> RM {selectedClaim.amount?.toFixed(2)}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Type:</strong> {selectedClaim.claim_type}
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-slate-900 dark:text-slate-100">
                {(reviewAction === 'rejected' || reviewAction === 'returned') ? 'Remarks (Required)' : 'Comments (Optional)'}
              </Label>
              <Textarea
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder={
                  reviewAction === 'rejected' ? 'Please provide reason for rejection...' :
                  reviewAction === 'returned' ? 'Please specify what needs to be corrected...' :
                  'Add any comments...'
                }
                className="mt-2 min-h-[100px] dark:bg-slate-900 dark:border-slate-600"
              />
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={submitReview}
                disabled={reviewLoading || ((reviewAction === 'rejected' || reviewAction === 'returned') && !reviewComments.trim())}
                className={
                  reviewAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' :
                  reviewAction === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-orange-600 hover:bg-orange-700'
                }
              >
                {reviewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {reviewAction === 'approved' && 'Approve'}
                {reviewAction === 'rejected' && 'Reject'}
                {reviewAction === 'returned' && 'Return'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
